import { useMemo, useCallback } from "react";
import FormValidator from "utils/FormValidator";
import {
    toMinutes,
    isFiniteNum,
    isOvernightShift,
    normalizeTimeSlots,
    alignToBase,
} from "@/utils";
import {
    DayName,
    DayInputs,
    DayErrorsMap,
    DayValidationErrors,
    ValidationError,
    TimeInput,
    WorkingHourInput,
} from "context/schedule/types";

const DAYS: DayName[] = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
];

export const useScheduleValidation = () => {
    // ============================================================================
    // VALIDATION RULES
    // ============================================================================

    const slotRules = useMemo(
        () => [
            {
                field: "start",
                method: "isLength",
                args: [{ min: 1 }],
                validWhen: true,
                message: "Start time is required",
            },
            {
                field: "end",
                method: "isLength",
                args: [{ min: 1 }],
                validWhen: true,
                message: "End time is required",
            },
            {
                field: "cutoff_time",
                method: "isLength",
                args: [{ min: 1 }],
                validWhen: true,
                message: "Cutoff time is required",
            },
            {
                field: "end",
                method: (value: string, args: { start: string }) => {
                    const startMinutes = toMinutes(args?.start);
                    const endMinutes = toMinutes(value);

                    // Use isFiniteNum so 00:00 (0) is not treated as invalid
                    if (!isFiniteNum(startMinutes) || !isFiniteNum(endMinutes))
                        return false;

                    // Align end relative to start; if still <=, add 24h to allow overnight/24h ranges
                    const alignedStart = startMinutes!;
                    let alignedEnd = alignToBase(endMinutes!, alignedStart);
                    if (alignedEnd <= alignedStart) alignedEnd += 1440;

                    return alignedEnd > alignedStart;
                },
                validWhen: true,
                message:
                    "End time must be after start time (allows overnight shifts)",
            },
            {
                field: "cutoff_time",
                // We also need the start/end to align cutoff on the same timeline as the slot
                method: (
                    value: string,
                    args: { start: string; end: string },
                ) => {
                    const startMin = toMinutes(args?.start);
                    const endMin = toMinutes(args?.end);
                    const cutoffMin = toMinutes(value);

                    if (
                        !isFiniteNum(startMin) ||
                        !isFiniteNum(endMin) ||
                        !isFiniteNum(cutoffMin)
                    ) {
                        return false;
                    }

                    const start = startMin; // 0..1439
                    const end = endMin; // 0..1439
                    const cutoff = cutoffMin; // 0..1439

                    const crossesMidnight = end <= start;

                    // 1) Slot crosses midnight: ej. 23:00 -> 01:00
                    //    We interpret 'end' as the next day (end + 1440).
                    //    If cutoff is in the morning (<= end), we also move it to the next day.
                    if (crossesMidnight) {
                        const alignedEnd = end + 1440;
                        const alignedCutoff =
                            cutoff + (cutoff <= end ? 1440 : 0);
                        return alignedCutoff < alignedEnd; // << Compare against aligned end
                    }

                    // 2) Slot does not cross midnight: ej. 09:00 -> 17:00 o 00:00 -> 01:00
                    //    Special case: if the slot starts exactly at midnight (00:00)
                    //    and the cutoff is from the previous night (e.g. 23:30), we align it to the previous day.
                    if (start === 0 && cutoff > end) {
                        const cutoffPrevDay = cutoff - 1440; // 23:30 -> -30
                        return cutoffPrevDay < end; // e.g. -30 < 60  => valid
                    }

                    // Direct rule
                    return cutoff < end;
                },
                validWhen: true,
                message: "Cutoff must be before end",
            },
        ],
        [],
    );

    const workingHourRules = useMemo(
        () => [
            {
                field: "start",
                method: (value: string, args: { end: string }) => {
                    const endProvided = args?.end;
                    return !endProvided || !!value;
                },
                validWhen: true,
                message: "Start time is required if end time is set",
            },
            {
                field: "end",
                method: (value: string, args: { start: string }) => {
                    const startProvided = args?.start;
                    return !startProvided || !!value;
                },
                validWhen: true,
                message: "End time is required if start time is set",
            },
        ],
        [],
    );

    const slotValidator = useMemo(
        () => new FormValidator(slotRules),
        [slotRules],
    );
    const workingHourValidator = useMemo(
        () => new FormValidator(workingHourRules),
        [workingHourRules],
    );

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================

    const extractErrors = useCallback(
        (validationResult: any): ValidationError => {
            const errors: ValidationError = {};
            Object.keys(validationResult).forEach(key => {
                const field = validationResult[key];
                if (key !== "isValid" && field && field.isInvalid) {
                    errors[key] = field.message;
                }
            });
            return errors;
        },
        [],
    );

    const toMin = useCallback(
        (t?: string | null) => toMinutes((t || "").trim()),
        [],
    );

    // ============================================================================
    // DAY VALIDATION
    // ============================================================================

    const runDayValidation = useCallback(
        (
            snapshot: DayInputs,
            disableTimeWindows: boolean,
        ): { mergedErrors: DayValidationErrors; hasInvalid: boolean } => {
            const mergedErrors: DayValidationErrors = {};
            let hasInvalid = false;

            const workingHours = snapshot.working_hours;
            const timeSlotsRaw = snapshot.time_slots ?? [];

            const generalMessages: string[] = [];
            const pushGeneral = (msg: string) => {
                if (msg && !generalMessages.includes(msg))
                    generalMessages.push(msg);
            };

            // Validate presence/shape of working hours (start/end pairing)
            const workingHoursResult = workingHourValidator.validate(
                workingHours ?? {},
            );
            if (!workingHoursResult.isValid) {
                hasInvalid = true;
                Object.assign(mergedErrors, extractErrors(workingHoursResult));
            }

            const hasAnyWorkingHours =
                !!workingHours && (!!workingHours.start || !!workingHours.end);
            const hasStartAndEnd = !!(workingHours?.start && workingHours?.end);

            // If working hours exist and time windows are enabled → require at least one slot
            if (
                !disableTimeWindows &&
                hasAnyWorkingHours &&
                timeSlotsRaw.length === 0
            ) {
                hasInvalid = true;
                pushGeneral("At least one time slot is required.");
            }

            // Working-hour boundaries in minutes
            const workingStartMin = toMin(workingHours?.start);
            let workingEndMin = toMin(workingHours?.end);

            // If overnight (or 24h), push end to the next day by +1440 minutes
            const isOvernightOrFullDay = isOvernightShift(
                workingHours?.start,
                workingHours?.end,
            );
            if (isOvernightOrFullDay && isFiniteNum(workingEndMin)) {
                workingEndMin += 1440;
            }

            // Per-slot validation (shape + within-working-hours boundaries)
            timeSlotsRaw.forEach((slot: TimeInput, index: number) => {
                // Schema/field validation for the slot
                const slotShape = slotValidator.validate(slot);
                if (!slotShape.isValid) {
                    hasInvalid = true;
                    mergedErrors[index] = {
                        ...((mergedErrors[index] as ValidationError) || {}),
                        ...extractErrors(slotShape),
                    };
                }

                // When windows are enabled and WH are complete, enforce slot bounds inside WH
                if (
                    !disableTimeWindows &&
                    hasStartAndEnd &&
                    slot?.start &&
                    slot?.end
                ) {
                    const base = workingStartMin as number; // WH start in minutes

                    const slotStartRaw = toMin(slot.start);
                    const slotEndRaw = toMin(slot.end);

                    const alignedSlotStart = alignToBase(slotStartRaw, base);
                    let alignedSlotEnd = alignToBase(slotEndRaw, base);
                    if (alignedSlotEnd <= alignedSlotStart)
                        alignedSlotEnd += 1440;

                    const alignedWorkingStart = base;
                    const alignedWorkingEnd = workingEndMin as number; // already +1440 if overnight/24h

                    const startsBeforeWorkingStart =
                        alignedSlotStart < alignedWorkingStart;
                    const endsAfterWorkingEnd =
                        alignedSlotEnd > alignedWorkingEnd;

                    if (startsBeforeWorkingStart || endsAfterWorkingEnd) {
                        hasInvalid = true;
                        const fieldErrors =
                            (mergedErrors[index] as ValidationError) || {};
                        if (startsBeforeWorkingStart) {
                            fieldErrors.start =
                                "Time slot start cannot be before working hours start.";
                        }
                        if (endsAfterWorkingEnd) {
                            fieldErrors.end =
                                "Time slot end cannot be after working hours end.";
                        }
                        mergedErrors[index] = fieldErrors;
                        pushGeneral(
                            "All time slots must be within working hours.",
                        );
                    }
                }
            });

            // Normalize slots to a single aligned timeline for coverage/overlap checks
            const normalizedSlots = normalizeTimeSlots(
                timeSlotsRaw,
                workingHours?.start,
            );

            // Coverage over the whole working window
            if (!disableTimeWindows && hasStartAndEnd) {
                if (normalizedSlots.length === 0) {
                    hasInvalid = true;
                    pushGeneral(
                        "Active time slots with start/end are required to cover working hours.",
                    );
                } else {
                    let earliestStart = normalizedSlots[0].start;
                    let latestEnd = normalizedSlots[0].end;

                    for (let i = 1; i < normalizedSlots.length; i++) {
                        const n = normalizedSlots[i];
                        if (n.start < earliestStart) earliestStart = n.start;
                        if (n.end > latestEnd) latestEnd = n.end;
                    }

                    if (
                        earliestStart > workingStartMin ||
                        latestEnd < workingEndMin
                    ) {
                        hasInvalid = true;
                        pushGeneral(
                            "Time slots must fully cover the working hours.",
                        );
                    }
                }
            }

            // Overlap and gap checks (on the aligned/normalized timeline)
            if (normalizedSlots.length > 0) {
                // Gap at the beginning of the window
                if (
                    !disableTimeWindows &&
                    hasStartAndEnd &&
                    normalizedSlots[0].start > workingStartMin
                ) {
                    hasInvalid = true;
                    pushGeneral(
                        "There is uncovered time before the first slot.",
                    );
                    const firstIdx = normalizedSlots[0].idx;
                    mergedErrors[firstIdx] = {
                        ...((mergedErrors[firstIdx] as ValidationError) || {}),
                        start: "First slot must start at working hour start.",
                    };
                }

                // Check each consecutive pair for overlap or gap
                let previousSlot = normalizedSlots[0];
                let previousEnd = previousSlot.end;

                for (let i = 1; i < normalizedSlots.length; i++) {
                    const currentSlot = normalizedSlots[i];
                    const currentStart = currentSlot.start;
                    const currentEnd = currentSlot.end;

                    // Overlap
                    if (currentStart < previousEnd) {
                        hasInvalid = true;
                        pushGeneral("Time slots cannot overlap.");
                        mergedErrors[previousSlot.idx] = {
                            ...((mergedErrors[
                                previousSlot.idx
                            ] as ValidationError) || {}),
                            end: "Overlaps with the next slot.",
                        };
                        mergedErrors[currentSlot.idx] = {
                            ...((mergedErrors[
                                currentSlot.idx
                            ] as ValidationError) || {}),
                            start: "Overlaps with the previous slot.",
                        };
                    }
                    // Gap between slots (only enforced when windows are enabled and WH complete)
                    else if (
                        !disableTimeWindows &&
                        hasStartAndEnd &&
                        currentStart > previousEnd
                    ) {
                        hasInvalid = true;
                        pushGeneral(
                            "There is uncovered time between consecutive slots.",
                        );
                    }

                    // Advance the "previous" pointer
                    if (currentEnd > previousEnd) {
                        previousEnd = currentEnd;
                        previousSlot = currentSlot;
                    }
                }

                // Gap at the end of the window
                const lastSlot = normalizedSlots[normalizedSlots.length - 1];
                if (
                    !disableTimeWindows &&
                    hasStartAndEnd &&
                    lastSlot.end < workingEndMin
                ) {
                    hasInvalid = true;
                    pushGeneral("There is uncovered time after the last slot.");
                    mergedErrors[lastSlot.idx] = {
                        ...((mergedErrors[lastSlot.idx] as ValidationError) ||
                            {}),
                        end: "Last slot must end at working hour end.",
                    };
                }
            }

            if (generalMessages.length) {
                mergedErrors.general = generalMessages.join(" ");
            }

            return { mergedErrors, hasInvalid };
        },
        [slotValidator, workingHourValidator, extractErrors, toMin],
    );

    // ============================================================================
    // WEEK VALIDATION
    // ============================================================================

    const validateAllDays = useCallback(
        (
            getDaySnapshot: (day: DayName) => DayInputs,
            disableTimeWindows: boolean,
        ): {
            nextErrors: DayErrorsMap;
            hasErrors: number;
            firstBadIndex: number | null;
        } => {
            const nextErrors: DayErrorsMap = {
                MONDAY: {},
                TUESDAY: {},
                WEDNESDAY: {},
                THURSDAY: {},
                FRIDAY: {},
                SATURDAY: {},
                SUNDAY: {},
            };

            let firstBadIndex: number | null = null;
            let daysWithErrors = 0;

            DAYS.forEach((day, index) => {
                const snapshot = getDaySnapshot(day);
                const { mergedErrors, hasInvalid } = runDayValidation(
                    snapshot,
                    disableTimeWindows,
                );
                nextErrors[day] = mergedErrors;
                if (hasInvalid) daysWithErrors += 1;
                if (hasInvalid && firstBadIndex === null) firstBadIndex = index;
            });

            return { nextErrors, hasErrors: daysWithErrors, firstBadIndex };
        },
        [runDayValidation],
    );

    // ============================================================================
    // WORKING HOURS VALIDATION
    // ============================================================================

    const hasAnyWorkingHoursInWeek = useCallback(
        (
            workingHours: (WorkingHourInput & { day_of_the_week: DayName })[],
        ): boolean => workingHours?.some(w => !!w?.start && !!w?.end),
        [],
    );

    return {
        // Validators
        slotValidator,
        workingHourValidator,

        // Validation functions
        runDayValidation,
        validateAllDays,
        hasAnyWorkingHoursInWeek,

        // Helpers
        extractErrors,
        DAYS,
    };
};
